import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { diaTrabalhoService } from '../../services';
import {
  DiaTrabalhoCardapioEntry,
  DiaTrabalhoCardapioInsumo,
  DiaTrabalhoCardapioReport,
  DiaTrabalhoCardapioSubprocesso,
  DiaTrabalhoCardapioSubproduto,
  DiaTrabalhoProdutoPlanejamento,
  DiaTrabalhoResumoProduto,
  SituacaoProducaoDiaTrabalho,
} from '../../types/producao';
import './DiaTrabalhoReportPage.css';

const situacaoLabels: Record<SituacaoProducaoDiaTrabalho, string> = {
  ABERTO: 'Aberto',
  PRODUZIDO: 'Produzido',
  CANCELADO: 'Cancelado',
};

const situacaoBadge: Record<SituacaoProducaoDiaTrabalho, string> = {
  ABERTO: 'warning',
  PRODUZIDO: 'success',
  CANCELADO: 'secondary',
};

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR');
};

const toNumber = (value?: string | number | null): number => {
  if (value == null || value === '') return 0;
  const numeric = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatQuantity = (value?: string | number | null): string => {
  if (value == null || value === '') return '-';
  const numeric = toNumber(value);
  return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};

const formatMinutes = (value?: string | number | null): string => {
  if (value == null || value === '') return '-';
  return `${formatQuantity(value)} min`;
};

const EtapasTable: React.FC<{ etapas?: DiaTrabalhoCardapioEntry['etapas'] }> = ({ etapas }) => {
  if (!etapas?.length) return null;
  const sorted = [...etapas].sort((a, b) => (a.sequencia ?? 0) - (b.sequencia ?? 0));

  return (
    <div className="report-section-block">
      <h6 className="report-section-title">Etapas</h6>
      <Table responsive hover size="sm" className="mb-0 align-middle">
        <thead>
          <tr>
            <th>Seq</th>
            <th>Descricao</th>
            <th>Observacao</th>
            <th>Maquina</th>
            <th className="text-end">Setup</th>
            <th className="text-end">Maquina</th>
            <th className="text-end">Homem</th>
            <th className="text-end">Espera</th>
            <th className="text-end">Ops.</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((etapa, index) => (
            <tr key={`${etapa.sequencia ?? index}-${etapa.descricao ?? 'etapa'}`}>
              <td>{etapa.sequencia ?? '-'}</td>
              <td>{etapa.descricao ?? '-'}</td>
              <td>{etapa.observacao || '-'}</td>
              <td>{etapa.maquina?.nome ?? '-'}</td>
              <td className="text-end">{formatQuantity(etapa.tempo_setup_maquina)}</td>
              <td className="text-end">{formatQuantity(etapa.tempo_maquina_min)}</td>
              <td className="text-end">{formatQuantity(etapa.tempo_homem_min)}</td>
              <td className="text-end">{formatQuantity(etapa.tempo_espera_min)}</td>
              <td className="text-end">{etapa.qtd_operadores ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const InsumosTable: React.FC<{ insumos?: DiaTrabalhoCardapioEntry['insumos'] }> = ({ insumos }) => {
  if (!insumos?.length) return null;

  return (
    <div className="report-section-block">
      <h6 className="report-section-title">Insumos</h6>
      <Table responsive hover size="sm" className="mb-0 align-middle">
        <thead>
          <tr>
            <th>Produto</th>
            <th className="text-end">Teorica</th>
            <th className="text-end">Consumida</th>
            <th className="text-end">Total</th>
            <th>Unidade</th>
          </tr>
        </thead>
        <tbody>
          {insumos.map((item: DiaTrabalhoCardapioInsumo, index) => (
            <tr
              key={`${item.produto?.id ?? 'produto'}-${item.produto?.codigo ?? 'sem-codigo'}-${index}`}
            >
              <td>{item.produto?.nome ?? item.produto?.codigo ?? '-'}</td>
              <td className="text-end">{formatQuantity(item.quantidade_teorica)}</td>
              <td className="text-end">{formatQuantity(item.quantidade_consumida)}</td>
              <td className="text-end">{formatQuantity(item.quantidade_total)}</td>
              <td>{item.unidade ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const SubprocessosTable: React.FC<{ subprocessos?: DiaTrabalhoCardapioSubprocesso[] }> = ({
  subprocessos,
}) => {
  if (!subprocessos?.length) return null;

  return (
    <div className="report-section-block">
      <h6 className="report-section-title">Subprocessos</h6>
      <Table responsive hover size="sm" className="mb-0 align-middle">
        <thead>
          <tr>
            <th>Seq</th>
            <th>Codigo</th>
            <th>Titulo</th>
            <th className="text-end">Quantidade</th>
            <th>Unidade</th>
          </tr>
        </thead>
        <tbody>
          {subprocessos.map((sub) => (
            <tr key={`${sub.id}-${sub.sequencia}`}>
              <td>{sub.sequencia}</td>
              <td>{sub.codigo ?? '-'}</td>
              <td>{sub.titulo ?? '-'}</td>
              <td className="text-end">{formatQuantity(sub.quantidade)}</td>
              <td>{sub.unidade ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const SubprodutosTable: React.FC<{ subprodutos?: DiaTrabalhoCardapioSubproduto[] }> = ({
  subprodutos,
}) => {
  if (!subprodutos?.length) return null;

  return (
    <div className="report-section-block">
      <h6 className="report-section-title">Subprodutos</h6>
      <Table responsive hover size="sm" className="mb-0 align-middle">
        <thead>
          <tr>
            <th>Codigo</th>
            <th>Titulo</th>
            <th className="text-end">Quantidade</th>
            <th>Unidade</th>
          </tr>
        </thead>
        <tbody>
          {subprodutos.map((sub, index) => (
            <tr key={`${sub.codigo ?? 'codigo'}-${sub.titulo ?? 'titulo'}-${index}`}>
              <td>{sub.codigo ?? '-'}</td>
              <td>{sub.titulo ?? '-'}</td>
              <td className="text-end">{formatQuantity(sub.quantidade)}</td>
              <td>{sub.unidade ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const ProdutosResumoTable: React.FC<{ produtos?: DiaTrabalhoResumoProduto[] }> = ({ produtos }) => {
  if (!produtos?.length) return null;

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Body>
        <Card.Title className="report-card-title">Produtos totais</Card.Title>
        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Codigo</th>
              <th>Nome</th>
              <th className="text-end">Quantidade</th>
              <th>Unidade</th>
            </tr>
          </thead>
          <tbody>
            {produtos.map((produto, index) => (
              <tr key={`${produto.produto_id ?? 'produto'}-${produto.codigo ?? 'codigo'}-${produto.unidade ?? 'un'}-${index}`}>
                <td>{produto.codigo ?? '-'}</td>
                <td>{produto.nome ?? '-'}</td>
                <td className="text-end">{formatQuantity(produto.quantidade_total)}</td>
                <td>{produto.unidade ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const ProdutosPlanejamentoTable: React.FC<{ produtos?: DiaTrabalhoProdutoPlanejamento[] }> = ({
  produtos,
}) => {
  if (!produtos?.length) return null;

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Body>
        <Card.Title className="report-card-title">Produtos de planejamento</Card.Title>
        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Codigo</th>
              <th>Nome</th>
              <th className="text-end">Planejada</th>
              <th className="text-end">Produzida</th>
              <th className="text-end">Sobra</th>
              <th>Unidade</th>
            </tr>
          </thead>
          <tbody>
            {produtos.map((produto, index) => (
              <tr key={`${produto.ficha_id ?? 'ficha'}-${produto.produto_id ?? 'produto'}-${index}`}>
                <td>{produto.codigo ?? '-'}</td>
                <td>{produto.nome ?? '-'}</td>
                <td className="text-end">{formatQuantity(produto.quantidade_planejada)}</td>
                <td className="text-end">{formatQuantity(produto.quantidade_produzida)}</td>
                <td className="text-end">{formatQuantity(produto.sobra)}</td>
                <td>{produto.unidade ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const DiaTrabalhoReportPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<DiaTrabalhoCardapioReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Nenhum dia selecionado.');
      return;
    }

    setLoading(true);
    setError(null);
    diaTrabalhoService
      .relatorio(id)
      .then((data) => setReport(data))
      .catch(() => setError('Nao foi possivel carregar o relatorio.'))
      .finally(() => setLoading(false));
  }, [id]);

  const fichas = useMemo(() => report?.fichas_tecnicas ?? report?.cardapio ?? [], [report]);

  const kpis = useMemo(() => {
    const totais = fichas.reduce(
      (acc, item) => {
        acc.planejado += toNumber(item.quantidade_planejada);
        acc.produzido += toNumber(item.quantidade_produzida);
        acc.sobra += toNumber(item.sobra_prevista);
        acc.lotes += toNumber(item.lotes_necessarios);
        return acc;
      },
      { planejado: 0, produzido: 0, sobra: 0, lotes: 0 },
    );

    return [
      { label: 'Fichas', value: fichas.length.toLocaleString('pt-BR') },
      { label: 'Qtd planejada', value: formatQuantity(totais.planejado) },
      { label: 'Qtd produzida', value: formatQuantity(totais.produzido) },
      { label: 'Lotes necessarios', value: formatQuantity(totais.lotes) },
      { label: 'Sobra prevista', value: formatQuantity(totais.sobra) },
    ];
  }, [fichas]);

  return (
    <div className="container-fluid py-4 dia-trabalho-report">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h4 className="mb-1">Relatorio de dia de trabalho</h4>
          <p className="text-muted mb-0">
            {report ? `Dia ${report.dia_trabalho} • ${formatDate(report.data)}` : ''}
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin/producao/dias-trabalho')}>
          Voltar
        </Button>
      </div>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <div className="mt-2">Carregando relatorio...</div>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && report && (
        <>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <div className="report-meta-label">Dia</div>
                  <div className="report-meta-value">{report.dia_trabalho}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="report-meta-label">Data</div>
                  <div className="report-meta-value">{formatDate(report.data)}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="report-meta-label">Responsavel</div>
                  <div className="report-meta-value">{report.responsavel || '-'}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row className="g-3 mb-4">
            {kpis.map((item) => (
              <Col xs={6} lg={2} key={item.label}>
                <Card className="report-kpi-card border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="report-kpi-label">{item.label}</div>
                    <div className="report-kpi-value">{item.value}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {(report.resumo_tempo || (report.produtos_totais?.length ?? 0) > 0) && (
            <Row className="g-3 mb-4">
              {report.resumo_tempo && (
                <Col xs={12} lg={4}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Body>
                      <Card.Title className="report-card-title">Resumo de tempo</Card.Title>
                      <div className="report-time-list">
                        <div>
                          <div className="report-time-label">Total</div>
                          <div className="report-time-value">{formatMinutes(report.resumo_tempo.tempo_total_min)}</div>
                        </div>
                        <div>
                          <div className="report-time-label">Setup</div>
                          <div className="report-time-value">{formatMinutes(report.resumo_tempo.tempo_setup_min)}</div>
                        </div>
                        <div>
                          <div className="report-time-label">Producao</div>
                          <div className="report-time-value">{formatMinutes(report.resumo_tempo.tempo_producao_min)}</div>
                        </div>
                        <div>
                          <div className="report-time-label">Limpeza</div>
                          <div className="report-time-value">{formatMinutes(report.resumo_tempo.tempo_limpeza_min)}</div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
              {report.produtos_totais?.length ? (
                <Col xs={12} lg={8}>
                  <ProdutosResumoTable produtos={report.produtos_totais} />
                </Col>
              ) : null}
            </Row>
          )}

          {(report.produtos_planejamento?.length ?? 0) > 0 && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <ProdutosPlanejamentoTable produtos={report.produtos_planejamento} />
              </Col>
            </Row>
          )}

          {report.sobras_subprodutos?.length ? (
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body>
                <Card.Title className="report-card-title">Sobras e subprodutos</Card.Title>
                <Table responsive hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Codigo</th>
                      <th>Titulo</th>
                      <th className="text-end">Demanda</th>
                      <th className="text-end">Lotes</th>
                      <th className="text-end">Planejada</th>
                      <th className="text-end">Sobra</th>
                      <th>Unidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sobras_subprodutos.map((item, index) => (
                      <tr key={`${item.ficha_id ?? index}-${item.codigo ?? 'codigo'}-${item.unidade ?? 'un'}`}>
                        <td>{item.codigo ?? '-'}</td>
                        <td>{item.titulo ?? '-'}</td>
                        <td className="text-end">{formatQuantity(item.demanda_total)}</td>
                        <td className="text-end">{formatQuantity(item.lotes_necessarios)}</td>
                        <td className="text-end">{formatQuantity(item.quantidade_planejada)}</td>
                        <td className="text-end">{formatQuantity(item.sobra_prevista)}</td>
                        <td>{item.unidade ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ) : null}

          {fichas.length === 0 && <Alert variant="warning">Nao ha fichas tecnicas para este dia.</Alert>}

          {fichas.map((entry, index) => (
            <Card key={`${entry.ficha_id}-${entry.codigo ?? index}`} className="shadow-sm border-0 mb-3">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <h5 className="mb-1">{entry.codigo ?? `Ficha ${entry.ficha_id}`} - {entry.titulo ?? '-'}</h5>
                    <div className="text-muted small">
                      Produto final: {entry.produto_final?.nome ?? entry.produto_final?.codigo ?? '-'}
                    </div>
                    {entry.versao ? <div className="text-muted small">Versao: {entry.versao}</div> : null}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {(entry.situacoes ?? []).length ? (
                      (entry.situacoes ?? []).map((situacao) => {
                        const key = situacao as SituacaoProducaoDiaTrabalho;
                        return (
                          <Badge key={`${entry.ficha_id}-${situacao}`} bg={situacaoBadge[key] ?? 'secondary'}>
                            {situacaoLabels[key] ?? situacao}
                          </Badge>
                        );
                      })
                    ) : (
                      <Badge bg="light" text="dark">Sem situacao</Badge>
                    )}
                  </div>
                </div>

                <Row className="g-3 mb-3">
                  <Col xs={6} md={3}>
                    <div className="report-stat-label">Qtd total</div>
                    <div className="report-stat-value">{formatQuantity(entry.quantidade_total)}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="report-stat-label">Qtd planejada</div>
                    <div className="report-stat-value">{formatQuantity(entry.quantidade_planejada)}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="report-stat-label">Qtd produzida</div>
                    <div className="report-stat-value">{formatQuantity(entry.quantidade_produzida)}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <div className="report-stat-label">Sobra prevista</div>
                    <div className="report-stat-value">{formatQuantity(entry.sobra_prevista)}</div>
                  </Col>
                </Row>

                <Row className="g-3 mb-2">
                  <Col xs={12} md={6} lg={3}>
                    <div className="report-inline-text"><strong>Rendimento:</strong> {formatQuantity(entry.rendimento?.quantidade)} {entry.rendimento?.unidade ?? ''}</div>
                  </Col>
                  <Col xs={12} md={6} lg={3}>
                    <div className="report-inline-text"><strong>Lotes:</strong> {formatQuantity(entry.lotes_necessarios)}</div>
                  </Col>
                  <Col xs={12} md={6} lg={3}>
                    <div className="report-inline-text"><strong>Tempo total:</strong> {formatMinutes(entry.tempo_total_planejado_min ?? entry.tempo_total_min)}</div>
                  </Col>
                  <Col xs={12} md={6} lg={3}>
                    <div className="report-inline-text"><strong>Setup:</strong> {formatMinutes(entry.tempo_setup_planejado_min ?? entry.tempo_setup_min)}</div>
                  </Col>
                </Row>

                {entry.instrucoes_producao && (
                  <div className="report-section-block">
                    <h6 className="report-section-title">Instrucoes de producao</h6>
                    <p className="mb-0 text-muted">{entry.instrucoes_producao}</p>
                  </div>
                )}
                {entry.observacoes_qualidade && (
                  <div className="report-section-block">
                    <h6 className="report-section-title">Observacoes de qualidade</h6>
                    <p className="mb-0 text-muted">{entry.observacoes_qualidade}</p>
                  </div>
                )}

                <EtapasTable etapas={entry.etapas} />
                <InsumosTable insumos={entry.insumos} />
                <SubprocessosTable subprocessos={entry.subprocessos} />
                <SubprodutosTable subprodutos={entry.subprodutos} />

                {entry.materiais_totais && (
                  <div className="report-section-block mb-0">
                    <h6 className="report-section-title">Materiais totais</h6>
                    <p className="mb-0">
                      {formatQuantity(entry.materiais_totais.quantidade)} {entry.materiais_totais.unidade ?? ''}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}

        </>
      )}

      {!loading && !report && !error && <Alert variant="warning">Nenhum relatorio encontrado para este dia.</Alert>}
    </div>
  );
};

export { DiaTrabalhoReportPage };
